/**
 * Interfaces
 */

import {CompanyResponse} from "../company/types";
import {CurrencyResponse} from "../currency/types";
import {OfferState, RealizationPlace} from "../offer/types";

/** Payment method. */
export enum PaymentMethod {
    Transfer = 0,
    Cash = 1
}

/** Offer summary response. */
export interface OfferSummaryResponse {
    /** Identity. */
    offerID: number;
    /** Number. */
    number?: string | undefined;
}


/** Invoice item request. */
export interface InvoiceItemRequest {
    /** Title. */
    title?: string | undefined;
    /** Description. */
    description?: string | undefined;
    /** Price. */
    price?: number;
}

/** Invoice item response. */
export interface InvoiceItemResponse {
    /** Title. */
    title?: string | undefined;
    /** Description. */
    description?: string | undefined;
    /** Price. */
    price?: number;
    /** Identity. */
    invoiceItemID: number;
    /** VAT. */
    vat: number | undefined;
    /** VAT rate. */
    vatRate: number | undefined;
    /** Price with VAT. */
    priceVat: number | undefined;
}

/** Invoice request. */
export interface InvoiceRequest {
    /** Number. */
    number?: string | undefined;
    /** Order Number. */
    orderNumber?: string | undefined;
    /** Offer Number. */
    offerNumber?: string | undefined;
    /** Issue date. */
    issueDate?: Date;
    /** Taxable transaction date. */
    taxableTransactionDate?: Date;
    /** Due date. */
    dueDate?: Date;
    /** Delivery note issue date. */
    deliveryNoteIssueDate?: Date;
    /** Realization date. */
    date?: string;
    /** Quote date. */
    quoteDate?: string;
    /** Commission Number. */
    commissionNumber?: string | undefined;
    /** Invoice Number. */
    invoiceNumber?: string | undefined;
    /** Exchange rate. */
    exchangeRate?: number;
    /** Note. */
    note?: string | undefined;
    /** Company ID FK. */
    companyID?: number | undefined;
    /** Currency ID FK. */
    currencyID?: number | undefined;
    invoiceState?: OfferState;
    realizationPlace?: RealizationPlace;
    /** Invoice items. */
    items?: InvoiceItemRequest[] | undefined;
    /** File identifiers to attach. */
    files?: number[] | undefined;
    /** Price. */
    price: number | undefined;
    /** VAT. */
    vat: number | undefined;
    /** Price with VAT. */
    priceVat: number | undefined;
    /** Transferred VAT. */
    transferredVat: number | undefined;
    /** Payment method. */
    paymentMethod?: PaymentMethod;
}

/** Invoice response. */
export interface InvoiceResponse {
    /** Number. */
    number?: string | undefined;
    /** Order Number. */
    orderNumber?: string | undefined;
    /** Offer Number. */
    offerNumber?: string | undefined;
    /** Issue date. */
    issueDate?: Date;
    /** Taxable transaction date. */
    taxableTransactionDate?: Date;
    /** Due date. */
    dueDate?: Date;
    /** Delivery note issue date. */
    deliveryNoteIssueDate?: Date;
    /** Paid date - set automatically when the invoice state changes to Paid. */
    paidDate?: Date;
    /** Invoice Number. */
    invoiceNumber?: string | undefined;
    /** Exchange rate. */
    exchangeRate?: number;
    /** Note. */
    note?: string | undefined;
    /** Company ID FK. */
    companyID?: number | undefined;
    /** Currency ID FK. */
    currencyID?: number | undefined;
    invoiceState?: InvoiceState;
    realizationPlace?: RealizationPlace;
    /** Invoice items. */
    items?: InvoiceItemResponse[] | undefined;
    /** Invoice files. */
    invoiceFiles?: InvoiceFileResponse[] | undefined;
    /** Price. */
    price: number | undefined;
    /** VAT. */
    vat: number | undefined;
    /** Price with VAT. */
    priceVat: number | undefined;
    /** Transferred VAT. */
    transferredVat: number | undefined;
    /** Payment method. */
    paymentMethod?: PaymentMethod;

    /** Identity. */
    invoiceID: number;
    company?: CompanyResponse;
    /** Offer */
    offer?: OfferSummaryResponse;
    /** Currency. */
    currency?: CurrencyResponse;
    /** Total amount. */
    total?: number | undefined;
}

export interface InvoiceFileResponse {
    /** Name. */
    name: string;
    /** Mime Type. */
    mimeType?: string | undefined;
    /** Line. */
    size?: number;
    /** Identity. */
    fileID: number;
    /** Identity GUID. */
    guid?: string;
    /** Invoice file Identity. */
    invoiceFileID: number;
}

export enum InvoiceState {
    New = "New",
    Send = "Send",
    Paid = "Paid",
}

export interface InvoiceTempPdf {
    /** Data. */
    data: Blob;
    /** Temp file Identifier. */
    id: number[];
}

/** Send mail API model. */
export interface SendMailDto {
    /** Subject. */
    subject: string;
    /** Body. */
    body?: string | undefined;
    /** Recipients. */
    recipients?: string[] | undefined;
    /** Recipients. */
    attachments?: number[] | undefined;
}

